import { useState, useEffect, useRef, useLayoutEffect } from 'react'
import { X } from 'lucide-react'
import { api } from '../services/api'
import type { AppBanner } from '../types'
import { Button } from './ui/Primitives'

const DISMISSED_KEY = (id: string) => `dhtn_banner_dismissed_${id}`
const OFFSET_VAR = '--dhtn-banner-offset'

export function AnnouncementBanner() {
    const [banner, setBanner] = useState<AppBanner | null>(null)
    const rootRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        api.getActiveBanner().then(b => {
            if (!b) return
            // Skip if the user already clicked this banner
            if (localStorage.getItem(DISMISSED_KEY(b.id))) return
            setBanner(b)
        })
    }, [])

    useLayoutEffect(() => {
        const el = rootRef.current
        if (!el || !banner) {
            document.documentElement.style.setProperty(OFFSET_VAR, '0px')
            return
        }

        const updateOffset = () => {
            document.documentElement.style.setProperty(OFFSET_VAR, `${el.offsetHeight}px`)
        }
        updateOffset()

        const observer = new ResizeObserver(updateOffset)
        observer.observe(el)
        window.addEventListener('resize', updateOffset)
        return () => {
            observer.disconnect()
            window.removeEventListener('resize', updateOffset)
            document.documentElement.style.setProperty(OFFSET_VAR, '0px')
        }
    }, [banner])

    if (!banner) return null

    const dismiss = () => {
        localStorage.setItem(DISMISSED_KEY(banner.id), '1')
        setBanner(null)
    }

    const handleCtaClick = () => {
        if (banner.ctaUrl) {
            window.open(banner.ctaUrl, '_blank', 'noopener,noreferrer')
        }
        dismiss()
    }

    return (
        <div ref={rootRef} role="alert" aria-live="polite" className="fixed inset-x-0 top-0 z-[55] w-full bg-yellow-400">
            <div className="relative mx-auto max-w-4xl px-4 py-3 pe-12 sm:pe-14">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    {/* The admin writes the banner in one language; dir="auto" keeps its punctuation on the right side in either page direction. */}
                    <p dir="auto" className="text-sm font-semibold leading-snug text-black sm:flex-1">
                        {banner.message}
                    </p>
                    {banner.ctaText && banner.ctaUrl && (
                        <Button
                            variant="primaryBlack"
                            size="sm"
                            fullWidth={false}
                            onClick={handleCtaClick}
                            className="!min-w-0 w-full !px-4 sm:w-auto sm:flex-shrink-0"
                        >
                            <bdi>{banner.ctaText}</bdi>
                        </Button>
                    )}
                </div>
                <button
                    onClick={dismiss}
                    aria-label="Dismiss"
                    className="absolute end-1 top-1 flex min-h-11 min-w-11 items-center justify-center rounded-control text-black/60 transition-colors hover:bg-black/10 hover:text-black"
                >
                    <X size={18} aria-hidden="true" />
                </button>
            </div>
        </div>
    )
}
